import type { AddressComponents } from '@/lib/address';
import { consumerUrl, opendoorUrl } from '@/lib/urls';

// Best-effort GET to the same-origin `/track/<keyword>` path that Beacon's
// `next.config.ts` rewrite proxies to Rails' partner-attribution endpoint.
//
// Same-origin (not `opendoorUrl(...)`) on purpose: a cross-origin call to
// `https://www.opendoor.com/track/...` from a Beacon origin
// (`beacon.apps(-staging).opendoor.com`) is blocked by Rails' CORS allow list
// for `/track/*` (`rb/services/web/config/initializers/cors.rb` only permits
// Reception + Cosmos origins), so the Set-Cookie silently drops and partner
// attribution is lost. Routing through the Beacon rewrite makes the request
// same-origin to the browser; Rails sets the `tracking` cookie with
// `domain: :all` (resolves to `.opendoor.com`), so the cookie covers every
// opendoor.com subdomain including beacon's.
//
// We also send `trackingKeywords` in the POST body below as defense-in-depth:
// Athena's `createSellerInputWithTrackingDataFromCookies` resolver spreads
// `...input` AFTER `trackingData: buildTrackingData(cookies)`, so an
// input-supplied `trackingData` wins. The cookie path remains for multi-touch
// attribution (Rails accumulates up to 5 recent keywords) and as a fallback
// when the body's `trackingKeywords` is absent.
//
// Owns its own AbortController + timeout so a slow or hung /track does not
// burn the POST's budget and — worse — cannot abort the POST's signal before
// it fires. A shared controller did the latter: when /track hung to the
// 10s timeout, abort() ran, this function swallowed the AbortError, and the
// POST then ran on an already-aborted signal and rejected immediately,
// silently dropping the lead. The whole point of this function is best-
// effort attribution that never blocks lead creation, so it must not share
// abort state with the POST.
const RECORD_PARTNER_REFERRAL_TIMEOUT_MS = 3_000;

async function recordPartnerReferral(trackingKeywords: string): Promise<void> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), RECORD_PARTNER_REFERRAL_TIMEOUT_MS);
  try {
    await fetch(`/track/${encodeURIComponent(trackingKeywords)}`, {
      method: 'GET',
      credentials: 'same-origin',
      signal: controller.signal,
    });
  } catch {
    // Best-effort: a failed or aborted /track call must not block lead
    // creation. The body's `trackingKeywords` (forwarded to Athena's
    // `trackingData` input by the cloudflare-worker handler) still attributes
    // this submission; only multi-touch history for future submissions is
    // lost, which is strictly better than dropping the lead.
  } finally {
    clearTimeout(timeoutId);
  }
}

export type SellerFlowChannel = 'HOME_VALUE' | 'DEFAULT';

// Source of truth for the seller-relationship wire enum. The Cloudflare worker
// declares the same set independently (it cannot import from beacon) but the
// shape is owned here, where the handoff request is constructed.
export const SELLER_RELATIONSHIP_VALUES = ['Self', 'Agent', 'AgentAndOwner', 'Other'] as const;
export type SellerRelationship = (typeof SELLER_RELATIONSHIP_VALUES)[number];

interface CreateSellerInputResponse {
  error?: boolean;
  uuid?: string;
}

function parseCreateSellerInputResponse(value: string): CreateSellerInputResponse {
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) return { error: true };
    return {
      ...('error' in parsed && typeof parsed.error === 'boolean' ? { error: parsed.error } : {}),
      ...('uuid' in parsed && typeof parsed.uuid === 'string' ? { uuid: parsed.uuid } : {}),
    };
  } catch {
    return { error: true };
  }
}

// Omitting `channel` matches the production homepage and lets Reception default
// to its DEFAULT channel; the estimator passes HOME_VALUE for attribution.
// `relationship` is forwarded to the worker, which chains an UpdateAnswers
// mutation so the confirmation modal's owner-relationship answer reaches
// Reception before the user lands on the seller-lead page.
//
// Bounded by `START_SELLER_FLOW_TIMEOUT_MS` so a stalled worker (cold start,
// network hang) cannot leave the caller's UI stuck in a “Confirming…” state
// forever — a timed-out fetch falls into the existing catch and redirects to
// `/address-entry`, matching the rest of the failure handling.
const START_SELLER_FLOW_TIMEOUT_MS = 10_000;

export async function startSellerFlow(
  components: AddressComponents,
  channel?: SellerFlowChannel,
  relationship?: SellerRelationship,
  trackingKeywords?: string,
  partnerReferralId?: string,
): Promise<void> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), START_SELLER_FLOW_TIMEOUT_MS);
  try {
    if (trackingKeywords) {
      await recordPartnerReferral(trackingKeywords);
    }
    const res = await fetch('/_address-input/create-seller-input', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        address: {
          street1: components.street,
          unit: components.unit ?? '',
          city: components.city,
          state: components.state,
          postal_code: components.postalCode,
        },
        ...(channel ? { channel } : {}),
        ...(relationship ? { relationship } : {}),
        // Explicit partner attribution that does not depend on the /track
        // cookie chain. The cloudflare-worker `handleCreateSellerInput`
        // forwards this into Athena's `input.trackingData`, where the
        // resolver's `...input` spread overrides the cookie-derived value.
        // Critical for Beacon origins (beacon.apps(-staging).opendoor.com)
        // where the cookie path may have failed silently due to CORS.
        ...(trackingKeywords ? { trackingKeywords } : {}),
        // Partner-referral attribution from an inbound `?partner_referral=<id>`
        // deep link (the partnership service redirects referral short links to
        // `<LandingPage>?partner_referral=<id>`). The cloudflare-worker
        // `handleCreateSellerInput` forwards this body field to Athena only
        // when present, mirroring cosmos's NewSellerFlowAddressInput.
        ...(partnerReferralId ? { partnerReferralId } : {}),
        analyticsMetadata: { product: 'SELL_DIRECT', url: window.location.href },
      }),
      signal: controller.signal,
    });
    const data = parseCreateSellerInputResponse(await res.text());
    if (!res.ok || data.error || !data.uuid) {
      throw new Error('create-seller-input failed');
    }
    window.location.href = consumerUrl(`/seller-lead/${data.uuid}`);
  } catch {
    window.location.href = opendoorUrl('/address-entry');
  } finally {
    clearTimeout(timeoutId);
  }
}
